import { useState, useRef, useEffect } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import {
  Trash2, GripVertical, Copy, ChevronUp, ChevronDown, Plus,
  Square, Columns2, Columns3, Columns4,
} from 'lucide-react';
import { cn } from '../../utils';
import { Tooltip } from '../../ui';
import { useEditorStore } from '../../store';
import type { DesignRow, DesignColumn, DesignElement } from '../../types/editor.types';
import { ElementRenderer } from './ElementRenderer';

const COLUMN_LAYOUTS = [
  { cols: 1, icon: Square, label: '1' },
  { cols: 2, icon: Columns2, label: '2' },
  { cols: 3, icon: Columns3, label: '3' },
  { cols: 4, icon: Columns4, label: '4' },
];

function MiniLayoutPicker({ afterRowId, onClose }: { afterRowId: string; onClose: () => void }) {
  const { addRowWithLayout } = useEditorStore();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  return (
    <div
      ref={ref}
      onClick={(e) => e.stopPropagation()}
      className="absolute z-50 left-1/2 -translate-x-1/2 bottom-full mb-1.5
        bg-surface dark:bg-dark-800 border border-line dark:border-dark-600
        rounded-xl shadow-elevated p-1.5"
    >
      <p className="text-[9px] font-semibold uppercase tracking-widest
        text-content-subtle dark:text-dark-500 text-center mb-1.5">
        Columns
      </p>
      <div className="flex gap-1">
        {COLUMN_LAYOUTS.map(({ cols, icon: Icon, label }) => (
          <button
            key={cols}
            title={`${cols} column${cols > 1 ? 's' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              addRowWithLayout(cols, afterRowId);
              onClose();
            }}
            className="flex flex-col items-center gap-1 px-2.5 py-1.5 rounded-lg
              text-content-muted dark:text-dark-400
              hover:bg-accent-muted dark:hover:bg-accent/10 hover:text-accent dark:hover:text-accent-light
              transition-all duration-100"
          >
            <Icon className="size-3.5" />
            <span className="text-[9px] font-medium">{label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function ElementWrapper({
  element, rowId, columnId, rowIsSelected,
}: { element: DesignElement; rowId: string; columnId: string; rowIsSelected: boolean }) {
  const { selectedElementId, selectElement, removeElement, duplicateElement, moveElement, design } = useEditorStore();
  const isSelected = selectedElementId === element.id;

  // Determine element position within its column for Up/Down enablement
  const col = design.body.rows.find(r => r.id === rowId)?.columns.find(c => c.id === columnId);
  const elementIndex = col?.elements.findIndex(e => e.id === element.id) ?? 0;
  const isFirst = elementIndex === 0;
  const isLast = elementIndex === (col?.elements.length ?? 1) - 1;
  const wrapperStyle: React.CSSProperties = {
    backgroundColor: (element.styles as Record<string, string>).backgroundColor ?? undefined,
    padding:         (element.styles as Record<string, string>).padding ?? undefined,
    margin:          (element.styles as Record<string, string>).margin ?? undefined,
    borderRadius:    (element.styles as Record<string, string>).borderRadius ?? undefined,
    border:          (element.styles as Record<string, string>).border ?? undefined,
  };

  const { attributes, listeners, setNodeRef, transform: _transform, transition: _transition, isDragging } = useSortable({
    id: element.id,
    data: { type: 'element', element, rowId, columnId },
  });

  return (
    <div
      ref={setNodeRef}
      style={wrapperStyle}
      onClick={(e) => { e.stopPropagation(); selectElement(element.id, rowId); }}
      className={cn(
        'relative group/el cursor-pointer rounded text-content-secondary dark:text-gray-400',
        'transition-shadow duration-150',
        isDragging && 'opacity-30',
        isSelected
          ? 'ring-1 ring-violet-500 ring-offset-1 ring-offset-white dark:ring-offset-dark-800'
          : !rowIsSelected && 'hover:ring-1 hover:ring-violet-200 dark:hover:ring-violet-700/40'
      )}
    >
      {/* Drag handle */}
      <div
        {...listeners}
        {...attributes}
        className="absolute -left-5 top-1/2 -translate-y-1/2
          opacity-0 group-hover/el:opacity-100 cursor-grab p-0.5
          text-gray-300 z-10"
      >
        <GripVertical className="size-3.5" />
      </div>

      {/* Element actions — Up/Down (when multiple elements in column) + Copy + Delete */}
      {isSelected && (
        <div className="absolute top-0.5 right-0.5 flex gap-1 z-20
          bg-violet-600 dark:bg-violet-700 rounded-md px-1 py-0.5 shadow-sm">
          {/* Up/Down only visible when there are sibling elements to move between */}
          {(col?.elements.length ?? 0) > 1 && (
            <>
              <Tooltip content="Move Up" side="top">
                <button
                  disabled={isFirst}
                  onClick={(e) => { e.stopPropagation(); moveElement(rowId, columnId, elementIndex, rowId, columnId, elementIndex - 1); }}
                  className="p-0.5 text-white hover:text-white/70 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronUp className="size-3" />
                </button>
              </Tooltip>
              <Tooltip content="Move Down" side="top">
                <button
                  disabled={isLast}
                  onClick={(e) => { e.stopPropagation(); moveElement(rowId, columnId, elementIndex, rowId, columnId, elementIndex + 1); }}
                  className="p-0.5 text-white hover:text-white/70 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronDown className="size-3" />
                </button>
              </Tooltip>
              <div className="w-px bg-violet-400/50 mx-0.5" />
            </>
          )}
          <Tooltip content="Duplicate" side="top">
            <button
              onClick={(e) => { e.stopPropagation(); duplicateElement(rowId, columnId, element.id); }}
              className="p-0.5 text-white hover:text-white/70 transition-colors"
            >
              <Copy className="size-3" />
            </button>
          </Tooltip>
          <Tooltip content="Delete" side="top">
            <button
              onClick={(e) => { e.stopPropagation(); removeElement(rowId, columnId, element.id); }}
              className="p-0.5 text-white hover:text-red-300 transition-colors"
            >
              <Trash2 className="size-3" />
            </button>
          </Tooltip>
        </div>
      )}

      <ElementRenderer element={element} editable={isSelected} />
    </div>
  );
}

function ColumnDropZone({ column, rowId, rowIsSelected }: {
  column: DesignColumn; rowId: string; columnCount: number; rowIsSelected: boolean
}) {
  const { isOver, setNodeRef } = useDroppable({
    id: `column:${rowId}:${column.id}`,
    data: { type: 'column', rowId, columnId: column.id },
  });

  const hasElements = column.elements.length > 0;

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'flex-1 min-w-0 min-h-16 relative rounded-sm transition-all duration-150 overflow-hidden',
        isOver
          ? 'bg-violet-50 dark:bg-violet-900/10 ring-2 ring-inset ring-violet-400/50 dark:ring-violet-500/40'
          : !hasElements && 'border border-dashed border-line/80 dark:border-dark-600/60',
      )}
    >
      {!hasElements && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <p className="text-[11px] font-medium text-gray-300 dark:text-dark-600">Drop here</p>
        </div>
      )}
      <SortableContext
        items={column.elements.map((e) => e.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className="p-1 space-y-1">
          {column.elements.map((el) => (
            <ElementWrapper key={el.id} element={el} rowId={rowId} columnId={column.id} rowIsSelected={rowIsSelected} />
          ))}
        </div>
      </SortableContext>
    </div>
  );
}

export function CanvasRow({ row }: { row: DesignRow }) {
  const { selectedRowId, selectElement, removeRow, duplicateRow, moveRow } = useEditorStore();
  const isSelected = selectedRowId === row.id && !useEditorStore.getState().selectedElementId;
  const [pickerOpen, setPickerOpen] = useState(false);

  return (
    <div
      onClick={(e) => { e.stopPropagation(); selectElement(null, row.id); }}
      style={row.styles as React.CSSProperties}
      className={cn(
        'relative group/row transition-all duration-150 rounded-lg',
        isSelected
          ? 'ring-2 ring-violet-400/50 dark:ring-violet-500/40 bg-violet-50/30 dark:bg-violet-900/[0.06]'
          : 'ring-0 hover:bg-gray-50/40 dark:hover:bg-dark-700/20'
      )}
    >
      {isSelected && (
        <div className="absolute top-2 right-2 z-20 flex gap-1
          bg-gray-900 dark:bg-dark-900 border border-gray-700/60 dark:border-dark-700
          rounded-lg px-1.5 py-0.5 shadow-elevated">
          <Tooltip content="Move Up" side="top">
            <button
              onClick={(e) => { e.stopPropagation(); moveRow(row.id, 'up'); }}
              className="p-0.5 text-white hover:text-gray-300 transition-colors"
            >
              <ChevronUp className="size-3" />
            </button>
          </Tooltip>
          <Tooltip content="Move Down" side="top">
            <button
              onClick={(e) => { e.stopPropagation(); moveRow(row.id, 'down'); }}
              className="p-0.5 text-white hover:text-gray-300 transition-colors"
            >
              <ChevronDown className="size-3" />
            </button>
          </Tooltip>
          <Tooltip content="Duplicate" side="top">
            <button
              onClick={(e) => { e.stopPropagation(); duplicateRow(row.id); }}
              className="p-0.5 text-white hover:text-gray-300 transition-colors"
            >
              <Copy className="size-3" />
            </button>
          </Tooltip>
          <div className="w-px bg-dark-600 mx-0.5" />
          <Tooltip content="Delete Row" side="top">
            <button
              onClick={(e) => { e.stopPropagation(); removeRow(row.id); }}
              className="p-0.5 text-white hover:text-red-400 transition-colors"
            >
              <Trash2 className="size-3" />
            </button>
          </Tooltip>
        </div>
      )}

      <div className={cn('flex', row.columns.length > 1 && 'gap-1.5')}>
        {row.columns.map((col) => (
          <ColumnDropZone
            key={col.id}
            column={col}
            rowId={row.id}
            columnCount={row.columns.length}
            rowIsSelected={isSelected}
          />
        ))}
      </div>

      <div className="absolute -bottom-3.5 left-1/2 -translate-x-1/2
        opacity-0 group-hover/row:opacity-100 transition-opacity z-10">
        <div className="relative">
          <button
            onClick={(e) => { e.stopPropagation(); setPickerOpen((p) => !p); }}
            className={cn(
              'flex items-center gap-1 text-[10px] font-semibold px-2.5 py-0.5 rounded-full',
              'border border-dashed border-violet-400/60 dark:border-violet-500/50',
              'text-violet-500 dark:text-violet-400',
              'bg-white/80 dark:bg-dark-800/80 backdrop-blur-sm',
              'shadow-sm transition-all duration-150',
              'hover:bg-violet-50 dark:hover:bg-violet-900/20',
              'hover:border-violet-500 dark:hover:border-violet-400',
              'hover:text-violet-700 dark:hover:text-violet-300',
              pickerOpen && 'bg-violet-50 dark:bg-violet-900/20 border-solid border-violet-500',
            )}
          >
            <Plus className="size-2.5" /> Add Row
          </button>
          {pickerOpen && (
            <MiniLayoutPicker afterRowId={row.id} onClose={() => setPickerOpen(false)} />
          )}
        </div>
      </div>
    </div>
  );
}
